// Custom React Hook for Firebase Authentication
import { useState, useEffect } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile as firebaseUpdateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser as firebaseDeleteUser,
  sendEmailVerification,
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';

export type UserRole = 'shopper' | 'business' | 'business_staff' | 'driver' | 'admin';

interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  role: UserRole;
  createdAt: Date;
  /** Set for business owners and for seats, pointing at their store. */
  businessId?: string;
  /** Customer on the FASH platform Stripe account, holding their saved cards. */
  stripeCustomerId?: string;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          const userDoc = await getDoc(doc(db, 'users', result.user.uid));
          if (!userDoc.exists()) {
            const profile: UserProfile = {
              uid: result.user.uid,
              email: result.user.email!,
              displayName: result.user.displayName,
              role: 'shopper',
              createdAt: new Date(),
            };
            await setDoc(doc(db, 'users', result.user.uid), profile);
          }
        }
      })
      .catch((err) => {
        console.error('Redirect sign-in error:', err);
      });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data() as UserProfile);
          }
        } catch (err: any) {
          console.error('Error fetching user profile:', err);
          setError(err.message);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    role: 'shopper' | 'business' | 'driver' = 'shopper'
  ) => {
    try {
      setError(null);
      
      // Create auth user
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      await firebaseUpdateProfile(result.user, { displayName });
      
      // Create user profile in Firestore
      const userProfile: UserProfile = {
        uid: result.user.uid,
        email: result.user.email!,
        displayName,
        role,
        createdAt: new Date(),
      };
      
      await setDoc(doc(db, 'users', result.user.uid), userProfile);
      
      setUserProfile(userProfile);
      return result.user;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const signInWithGoogle = async (defaultRole: 'shopper' | 'business' | 'driver' = 'shopper') => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();

      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupErr: any) {
        if (popupErr.code === 'auth/popup-closed-by-user') return null;
        if (
          popupErr.code === 'auth/popup-blocked' ||
          popupErr.code === 'auth/cancelled-popup-request'
        ) {
          await signInWithRedirect(auth, provider);
          return null;
        }
        throw popupErr;
      }

      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        const profile: UserProfile = {
          uid: result.user.uid,
          email: result.user.email!,
          displayName: result.user.displayName,
          role: defaultRole,
          createdAt: new Date(),
        };
        await setDoc(doc(db, 'users', result.user.uid), profile);
        setUserProfile(profile);
      } else {
        setUserProfile(userDoc.data() as UserProfile);
      }

      return result.user;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      setUserProfile(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const reauthenticate = async (password: string) => {
    if (!user || !user.email) throw new Error('No user signed in');
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null);
      await reauthenticate(currentPassword);
      await firebaseUpdatePassword(user!, newPassword);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateProfile = async (data: { displayName?: string; photoURL?: string }) => {
    if (!user) throw new Error('No user signed in');
    try {
      setError(null);
      await firebaseUpdateProfile(user, data);
      const updates: Record<string, any> = {};
      if (data.displayName !== undefined) updates.displayName = data.displayName;
      if (data.photoURL !== undefined) updates.photoURL = data.photoURL;
      await updateDoc(doc(db, 'users', user.uid), updates);
      if (userProfile) {
        setUserProfile({ ...userProfile, ...updates });
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const sendVerificationEmail = async () => {
    if (!user) throw new Error('No user signed in');
    try {
      setError(null);
      await sendEmailVerification(user);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteAccount = async (password: string) => {
    if (!user) throw new Error('No user signed in');
    try {
      setError(null);
      const hasPassword = user.providerData.some(p => p.providerId === 'password');
      if (hasPassword) {
        await reauthenticate(password);
      }
      await deleteDoc(doc(db, 'users', user.uid));
      await firebaseDeleteUser(user);
      setUserProfile(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  return {
    user,
    userProfile,
    loading,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    changePassword,
    updateProfile,
    sendVerificationEmail,
    deleteAccount,
    reauthenticate,
  };
}

